import React, { useRef, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Badge } from '../../components/Badge/Badge';
import { Card } from '../../components/Card';
import { AuditTimestamp } from './AuditTimestamp';
import type { AuditTimelineProps } from './AuditTimeline.types';
import type { AuditHistoryItem } from '../types/audit.types';

const MAX_FIELDS_PREVIEW = 3;

/**
 * `fieldsLabel` rotula la lista de campos de la tarjeta. Sin él, la tercera línea era un
 * «Estado» suelto: el usuario veía un sustantivo sin saber si era el estado de la entrada, un
 * campo, o el nombre del registro. El rótulo va por ACCIÓN porque no significan lo mismo — en un
 * alta los campos son los INICIALES, no unos «modificados».
 */
const ACTION_CONFIG: Record<
  AuditHistoryItem['action'],
  { color: 'green' | 'amber' | 'red'; label: string; fieldsLabel: string }
> = {
  Created: { color: 'green', label: 'Creado', fieldsLabel: 'Campos iniciales' },
  Modified: { color: 'amber', label: 'Actualizado', fieldsLabel: 'Campos modificados' },
  Deleted: { color: 'red', label: 'Eliminado', fieldsLabel: 'Campos afectados' },
};

function groupByDate(entries: AuditHistoryItem[]): Array<{ dateLabel: string; entries: AuditHistoryItem[] }> {
  const groups = new Map<string, AuditHistoryItem[]>();
  for (const entry of entries) {
    const date = new Date(entry.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let label: string;
    if (date.toDateString() === today.toDateString()) {
      label = 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      label = 'Ayer';
    } else {
      label = date.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(entry);
  }
  return Array.from(groups.entries()).map(([dateLabel, entries]) => ({ dateLabel, entries }));
}

const SkeletonCard: React.FC = () => (
  <Card className="gap-0 rounded-lg px-3 py-3">
    <div className="flex items-center gap-2 mb-2">
      <Skeleton width={64} height={20} borderRadius={4} />
      <Skeleton width={100} height={14} />
    </div>
    <Skeleton width={80} height={12} className="mb-1" />
    <Skeleton width={140} height={12} />
  </Card>
);

interface TimelineCardProps {
  entry: AuditHistoryItem;
  selected: boolean;
  onSelect: (id: string) => void;
}

const TimelineCard: React.FC<TimelineCardProps> = ({ entry, selected, onSelect }) => {
  const { color, label, fieldsLabel } = ACTION_CONFIG[entry.action];
  const previewFields = entry.changedFields.slice(0, MAX_FIELDS_PREVIEW);
  const extraCount = entry.changedFields.length - MAX_FIELDS_PREVIEW;

  const cardClasses = [
    'gap-0 rounded-lg px-3 py-3 cursor-pointer',
    'focus:outline-none focus:ring-2 focus:ring-primary-500',
    'transition-all duration-100',
    selected
      ? 'border-primary-500 ring-1 ring-primary-500 bg-[#f7fcff] dark:border-primary-600 dark:bg-primary-900/20'
      : 'hover:border-primary-300 dark:hover:border-primary-700',
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(entry.id);
    }
  };

  const handleClick = () => {
    // Un click-drag para seleccionar/copiar texto de la tarjeta también dispara este
    // "click" al soltar el mouse. Si el usuario dejó una selección de texto activa,
    // es que quería copiar, no cambiar la entrada seleccionada del historial.
    const selection = window.getSelection?.();
    if (selection && selection.toString().length > 0) return;
    onSelect(entry.id);
  };

  return (
    <Card
      role="listitem"
      aria-selected={selected}
      tabIndex={0}
      className={cardClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Jerarquía de la tarjeta, de mayor a menor: QUIÉN · CUÁNDO · QUÉ. Las tres líneas
          comparten el mismo margen inferior (`mb-1`) para que el ritmo vertical sea constante;
          antes la fecha compartía tamaño (`text-sm`) con el actor y las dos competían. */}
      <div className="flex items-center gap-2 mb-1">
        <Badge color={color} label={label} aria-label={`Acción: ${label}`} />
        <span className="min-w-0 flex-1 text-sm font-medium text-content-primary dark:text-dark-content-primary truncate">
          {entry.actorFullName}
        </span>
      </div>
      <AuditTimestamp timestamp={entry.timestamp} size="xs" className="block mb-1" />
      {previewFields.length > 0 && (
        <p className="text-xs text-content-secondary dark:text-dark-content-secondary truncate">
          <span className="text-content-tertiary dark:text-dark-content-tertiary">{fieldsLabel}: </span>
          {previewFields.join(', ')}
          {extraCount > 0 && (
            <span className="text-content-tertiary dark:text-dark-content-tertiary"> +{extraCount} más</span>
          )}
        </p>
      )}
    </Card>
  );
};

// ===== Paginador estilo MasterCrud (nav interno de la tabla del kit) =====
// Iconos (lucide, mismos paths que el paginador de la tabla del kit).
const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
);
const ChevronRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);
const ChevronsLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);
const ChevronsRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

const navIconBtnCls =
  'inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer text-primary-600 dark:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed';
const navNumBtnCls = (active: boolean) =>
  [
    'inline-flex items-center justify-center min-w-8 h-8 px-3 rounded-md cursor-pointer text-sm font-bold transition-all duration-150',
    active
      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-600'
      : 'text-primary-600 dark:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 active:scale-95',
  ].join(' ');

// Ventana de números idéntica a la del paginador de la tabla: hasta 3 páginas se muestran
// todas; con más, una ventana de 2 números (primera/última las cubren los chevrones dobles).
function getTimelinePageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const start = Math.min(Math.max(currentPage, 1), totalPages - 1);
  return [start, start + 1];
}

interface TimelinePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const TimelinePagination: React.FC<TimelinePaginationProps> = ({ currentPage, totalPages, onPageChange }) => (
  <nav className="flex items-center justify-center gap-0.5" aria-label="Navegación de páginas del historial">
    <button type="button" aria-label="Primera página" onClick={() => currentPage > 1 && onPageChange(1)} disabled={currentPage === 1} className={navIconBtnCls}>
      <ChevronsLeftIcon />
    </button>
    <button type="button" aria-label="Página anterior" onClick={() => currentPage > 1 && onPageChange(currentPage - 1)} disabled={currentPage === 1} className={navIconBtnCls}>
      <ChevronLeftIcon />
    </button>
    {getTimelinePageNumbers(currentPage, totalPages).map((p) => (
      <button
        key={p}
        type="button"
        onClick={() => onPageChange(p)}
        aria-current={p === currentPage ? 'page' : undefined}
        className={navNumBtnCls(p === currentPage)}
      >
        {p}
      </button>
    ))}
    <button type="button" aria-label="Página siguiente" onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className={navIconBtnCls}>
      <ChevronRightIcon />
    </button>
    <button type="button" aria-label="Última página" onClick={() => currentPage < totalPages && onPageChange(totalPages)} disabled={currentPage === totalPages} className={navIconBtnCls}>
      <ChevronsRightIcon />
    </button>
  </nav>
);

/**
 * Panel izquierdo del split view: lista cronológica de entradas de auditoría
 * con separadores de fecha, skeleton de carga y paginación.
 */
export const AuditTimeline: React.FC<AuditTimelineProps> = ({
  entries,
  selectedId,
  onSelect,
  isLoading = false,
  totalCount,
  page,
  pageSize,
  onPageChange,
  className = '',
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const containerClasses = ['flex flex-col h-full', className]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!entries.length) return;
      const currentIndex = entries.findIndex((en) => en.id === selectedId);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = entries[Math.min(currentIndex + 1, entries.length - 1)];
        if (next) onSelect(next.id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = entries[Math.max(currentIndex - 1, 0)];
        if (prev) onSelect(prev.id);
      }
    },
    [entries, selectedId, onSelect]
  );

  return (
    <div className={containerClasses}>
      {/* Lista */}
      <div
        ref={listRef}
        role="list"
        aria-label="Historial de cambios"
        // 16px, los MISMOS que el panel derecho (`px-4 pt-4` en su cabecera y `px-4 pb-4` en la
        // caja de la tabla). Iban a 12 y la diferencia se notaba justo donde más: al final del
        // scroll la última tarjeta quedaba a 12px del borde del modal mientras la tabla de al
        // lado respiraba 16. Medido en dev el 2026-09-17 sobre el modal abierto.
        className="flex-1 overflow-y-auto px-4 py-4"
        onKeyDown={handleKeyDown}
      >
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : entries.length === 0 ? (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center h-32 text-sm text-content-tertiary dark:text-dark-content-tertiary px-4 text-center"
          >
            No hay registros de auditoría para este elemento
          </div>
        ) : (
          groupByDate(entries).map(({ dateLabel, entries: group }) => (
            <React.Fragment key={dateLabel}>
              {/* Date separator */}
              {/* `-mx-4 px-4`: el separador es `sticky`, así que al hacer scroll las tarjetas pasan
                  POR DEBAJO. Con el fondo acabando donde acaba el contenido, se veían asomar por
                  los 16px laterales. Se extiende el fondo hasta el borde del scrollport. */}
              <div className="flex items-center gap-2 py-2 mt-2 first:mt-0 sticky top-0 -mx-4 px-4 bg-background-secondary dark:bg-dark-bg-primary z-10">
                <span className="text-xs font-medium text-content-secondary dark:text-dark-content-secondary uppercase tracking-wide">
                  {dateLabel}
                </span>
                <div className="flex-1 h-px bg-border-primary dark:bg-dark-border-primary" />
              </div>
              <div className="space-y-2">
                {group.map((entry) => (
                  <TimelineCard
                    key={entry.id}
                    entry={entry}
                    selected={entry.id === selectedId}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </React.Fragment>
          ))
        )}
      </div>

      {/* Pagination — mismo nav (chevrones + números) que el paginador de la tabla de MasterCrud. */}
      {!isLoading && totalPages > 1 && (
        <div className="flex-shrink-0 border-t border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-primary px-4 py-2">
          <TimelinePagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
};

AuditTimeline.displayName = 'AuditTimeline';
